import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Check, RotateCcw, X, Pencil, Bookmark, Quote } from 'lucide-react';
import type { Chapter, PlotThread, PlotThreadKind, PlotThreadStatus, Volume } from '../../types';
import {
  PLOT_THREAD_KINDS, PLOT_THREAD_KIND_LABELS, PLOT_THREAD_STATUS_LABELS,
} from '@xiaotianshu/shared';
import { useWorkspace } from '../workspace/WorkspaceContext';
import { notifyError, errorMessage, notifySuccess } from '../../utils/notify';
import { LoadError } from '../../components/LoadError';
import './styles/plotThreads.css';

type StatusFilter = 'all' | PlotThreadStatus;
/** 类型筛选：'all' = 全部等级（默认，方便一眼看全书埋了什么） */
type KindFilter = 'all' | PlotThreadKind;

/** 「全部」排第一，与下方类型筛选同构（两行第一个都是「全部」才好看） */
const STATUS_FILTERS: Array<{ id: StatusFilter; label: string }> = [
  { id: 'all', label: '全部' },
  { id: 'open', label: '未回收' },
  { id: 'resolved', label: '已回收' },
  { id: 'dropped', label: '已废弃' },
];

interface FormState {
  id?: string;
  title: string;
  kind: PlotThreadKind;
  detail: string;
  plantedChapterId: string;
}

const EMPTY_FORM: FormState = {
  title: '', kind: 'foreshadow', detail: '', plantedChapterId: '',
};

/**
 * 伏笔 / 线索追踪。
 *
 * 长篇写作的核心痛点：埋了伏笔要记得回收。此前只能把备注写在章节摘要里，
 * 跨几十万字必然遗漏。这里提供纯人工登记 + 未回收提醒（不依赖 AI）。
 *
 * 登记入口有两个：编辑器里选中正文右键「添加为伏笔」（会带上原文与位置，
 * 见 PlotThreadModal），以及本页顶部的「登记线索」（纯手工登记）。
 * 本页负责总览（按类型/状态筛选）与回收、废弃等生命周期操作。
 */
export function PlotThreads() {
  const {
    bookId, handleRequestChapterSwitch, dataRefreshTrigger, selectedChapter, jumpToQuote,
  } = useWorkspace();
  const [threads, setThreads] = useState<PlotThread[]>([]);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  // 默认「全部」：进来先看全貌（哪些还悬着、哪些已收、哪些放弃了），
  // 要聚焦时再点「未回收」。此前默认只看未回收，还要先切一次筛选才看到全貌。
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [kindFilter, setKindFilter] = useState<KindFilter>('all');
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!bookId) return;
    setLoading(true);
    try {
      const [rows, vols] = await Promise.all([
        window.api.getPlotThreads(bookId),
        window.api.getVolumes(bookId),
      ]);
      // 章节下拉需要全书章节（跨卷选"埋点"位置）
      const chapLists = await Promise.all((vols as Volume[]).map((v) => window.api.getChapters(v.id)));
      const all = chapLists.flat().sort((a, b) => a.chapter_number - b.chapter_number);
      setThreads(rows);
      setChapters(all);
      setLoadFailed(false);
    } catch (err) {
      setLoadFailed(true);
      notifyError('线索加载失败：' + errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [bookId]);

  useEffect(() => { void load(); }, [load, dataRefreshTrigger]);

  /** 先按类型、再按状态过滤（两个维度独立，用户可只看"未回收的伏笔"） */
  const visible = useMemo(() => threads.filter((t) => {
    if (kindFilter !== 'all' && t.kind !== kindFilter) return false;
    return filter === 'all' ? true : t.status === filter;
  }), [threads, filter, kindFilter]);

  const counts = useMemo(() => {
    const base: Record<StatusFilter, number> = { all: threads.length, open: 0, resolved: 0, dropped: 0 };
    for (const t of threads) base[t.status] += 1;
    return base;
  }, [threads]);

  /** 各类型的条数（用于筛选按钮上的角标；未回收/总数分别统计） */
  const kindCounts = useMemo(() => {
    const base: Record<KindFilter, number> = {
      all: threads.length, foreshadow: 0, subplot: 0, mystery: 0, promise: 0,
    };
    for (const t of threads) base[t.kind] += 1;
    return base;
  }, [threads]);

  /** 早埋未收的提醒：埋点章节远早于当前进度但状态仍为 open */
  const staleReminder = useMemo(() => {
    const open = threads.filter((t) => t.status === 'open');
    const withPlanted = open.filter((t) => typeof t.planted_chapter_number === 'number');
    if (withPlanted.length === 0) return 0;
    const maxChapter = chapters.reduce((m, c) => Math.max(m, c.chapter_number ?? 0), 0);
    // 超过 20 章仍未回收视为"久未回收"，提示作者回头看看
    return withPlanted.filter((t) => maxChapter - (t.planted_chapter_number ?? 0) > 20).length;
  }, [threads, chapters]);

  const chapterLabel = (id: string | null): string => {
    if (!id) return '';
    const ch = chapters.find((c) => c.id === id);
    if (!ch) return '（章节已删除）';
    return `第${ch.chapter_number}章 ${ch.title}`;
  };

  const startCreate = () => setForm({ ...EMPTY_FORM });

  const startEdit = (t: PlotThread) => setForm({
    id: t.id,
    title: t.title,
    kind: t.kind,
    detail: t.detail ?? '',
    plantedChapterId: t.planted_chapter_id ?? '',
  });

  const submit = async () => {
    if (!form) return;
    if (!form.title.trim()) {
      notifyError('请填写线索标题');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        kind: form.kind,
        title: form.title.trim(),
        detail: form.detail,
        plantedChapterId: form.plantedChapterId || null,
      };
      if (form.id) {
        await window.api.updatePlotThread({ id: form.id, ...payload });
      } else {
        await window.api.createPlotThread({ bookId, ...payload });
      }
      setForm(null);
      await load();
      notifySuccess(form.id ? '线索已更新' : '线索已登记');
    } catch (err) {
      notifyError('保存线索失败：' + errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  /**
   * 标记回收：默认记录到"当前正在编辑的章节"。
   *
   * 为什么默认当前章：回收这个动作几乎都发生在写到回收情节的那一刻，
   * 让用户再从下拉里翻一遍章节是纯粹的重复劳动。需要写别的位置时，
   * 用条目上的「改回收章节」下拉即可（传到 resolvedChapterId）。
   *
   * 注：曾经的"计划回收章节"已移除 —— 作者通常也说不好具体哪章回收，
   * 真要记就写在备注里，避免"计划 × 实际"两栏并排显示。
   */
  const markResolved = async (t: PlotThread, resolvedChapterId: string | null) => {
    try {
      await window.api.updatePlotThread({
        id: t.id,
        status: 'resolved',
        resolvedChapterId,
      });
      await load();
      notifySuccess(resolvedChapterId ? '已标记为回收' : '已标记为回收（未指定章节）');
    } catch (err) {
      notifyError('标记回收失败：' + errorMessage(err));
    }
  };

  const markOpen = async (t: PlotThread) => {
    try {
      await window.api.updatePlotThread({ id: t.id, status: 'open', resolvedChapterId: null });
      await load();
    } catch (err) {
      notifyError('恢复未回收失败：' + errorMessage(err));
    }
  };

  const drop = async (t: PlotThread) => {
    try {
      await window.api.updatePlotThread({ id: t.id, status: 'dropped' });
      await load();
    } catch (err) {
      notifyError('标记废弃失败：' + errorMessage(err));
    }
  };

  const remove = async (t: PlotThread) => {
    try {
      await window.api.deletePlotThread(t.id);
      await load();
    } catch (err) {
      notifyError('删除线索失败：' + errorMessage(err));
    }
  };

  /** 跳回正文：有引文与位置时连"当初标的是哪句话"一起定位 */
  const openInEditor = (t: PlotThread) => {
    if (!t.planted_chapter_id) return;
    if (!t.quote) {
      void handleRequestChapterSwitch(t.planted_chapter_id);
      return;
    }
    void jumpToQuote({
      chapterId: t.planted_chapter_id,
      text: t.quote,
      from: t.quote_start ?? null,
      to: t.quote_end ?? null,
    });
  };

  /** 回收按钮的提示文案：写明会记到哪一章，避免用户不确定副作用 */
  const resolveHint = selectedChapter
    ? `标记为已回收（记录到当前章节：第${selectedChapter.chapter_number}章）`
    : '标记为已回收';

  return (
    <div className="pt-root">
      {/*
        标题行：与「设定管理」那页的 `.ws-head`（40px、下边框、card 背景、加粗）**同一规格** ——
        用户指出"伏笔页没有标题行，而设定页有"，两页看起来不一致。
        页名移到这里之后，下面那行只留统计与「登记线索」按钮，不再重复页名。
      */}
      <div className="pt-titlebar">伏笔追踪</div>

      <div className="pt-head">
        <div className="pt-head-title">
          <span className="pt-head-sub">
            {counts.open} 条未回收 · 共 {counts.all} 条
            {staleReminder > 0 && <b className="pt-stale">（{staleReminder} 条已埋超过 20 章未回收）</b>}
          </span>
        </div>
        <button className="pt-primary" onClick={startCreate}>
          <Plus size={14} /> 登记线索
        </button>
      </div>

      <div className="pt-filters">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.id}
            className={`pt-filter${filter === f.id ? ' is-active' : ''}`}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
            <span className="pt-filter-count">{counts[f.id]}</span>
          </button>
        ))}
      </div>

      {/* 类型筛选：伏笔/支线/悬念/承诺各自成档，便于只盯某一类线索 */}
      <div className="pt-filters pt-kind-filters">
        <button
          className={`pt-filter${kindFilter === 'all' ? ' is-active' : ''}`}
          onClick={() => setKindFilter('all')}
        >
          全部类型
          <span className="pt-filter-count">{kindCounts.all}</span>
        </button>
        {PLOT_THREAD_KINDS.map((k) => (
          <button
            key={k}
            className={`pt-filter pt-filter-${k}${kindFilter === k ? ' is-active' : ''}`}
            onClick={() => setKindFilter(k)}
          >
            {PLOT_THREAD_KIND_LABELS[k]}
            <span className="pt-filter-count">{kindCounts[k]}</span>
          </button>
        ))}
      </div>

      {form && (
        <div className="pt-form">
          <div className="pt-form-row">
            <input
              className="pt-input"
              placeholder="线索标题（如：神秘的黑色玉佩）"
              value={form.title}
              autoFocus
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              aria-label="线索标题"
            />
            <select
              className="pt-select"
              value={form.kind}
              onChange={(e) => setForm({ ...form, kind: e.target.value as PlotThreadKind })}
              aria-label="线索类型"
            >
              {PLOT_THREAD_KINDS.map((k) => (
                <option key={k} value={k}>{PLOT_THREAD_KIND_LABELS[k]}</option>
              ))}
            </select>
          </div>
          <div className="pt-form-row">
            <label className="pt-field">
              埋于
              <select
                className="pt-select"
                value={form.plantedChapterId}
                onChange={(e) => setForm({ ...form, plantedChapterId: e.target.value })}
                aria-label="埋点章节"
              >
                <option value="">未指定</option>
                {chapters.map((c) => (
                  <option key={c.id} value={c.id}>第{c.chapter_number}章 {c.title}</option>
                ))}
              </select>
            </label>
          </div>
          <textarea
            className="pt-textarea"
            placeholder="备注（埋了什么、打算怎么回收）"
            value={form.detail}
            onChange={(e) => setForm({ ...form, detail: e.target.value })}
            aria-label="线索备注"
            rows={2}
          />
          <div className="pt-form-actions">
            <button className="pt-ghost" onClick={() => setForm(null)} disabled={saving}>
              <X size={13} /> 取消
            </button>
            <button
              className="pt-primary"
              onClick={() => void submit()}
              disabled={saving}
              aria-label={form.id ? '保存线索' : '确认登记'}
            >
              <Check size={13} /> {saving ? '保存中…' : (form.id ? '保存' : '登记')}
            </button>
          </div>
        </div>
      )}

      <div className="pt-list">
        {loading ? (
          <div className="pt-empty">加载中…</div>
        ) : loadFailed ? (
          <LoadError message="线索加载失败" onRetry={() => { void load(); }} />
        ) : visible.length === 0 ? (
          <div className="pt-empty">
            {/* 默认就是「全部」，所以这条空态文案是用户最常看到的那句：
                同时告知两个登记入口（本页登记 / 编辑器右键） */}
            {filter === 'all'
              ? '暂无线索。可在上方「登记线索」登记，或在编辑器里选中正文右键「添加为伏笔」。'
              : filter === 'open'
                ? '暂无未回收的线索，可在上方「登记线索」记录伏笔，或在编辑器里选中正文右键「添加为伏笔」。'
                : '该筛选条件下暂无线索。'}
          </div>
        ) : (
          visible.map((t) => (
            <div key={t.id} className={`pt-item pt-status-${t.status}`}>
              <div className="pt-item-main">
                <div className="pt-item-head">
                  <span className={`pt-kind pt-kind-${t.kind}`}>{PLOT_THREAD_KIND_LABELS[t.kind]}</span>
                  <span className="pt-item-title">{t.title}</span>
                  <span className={`pt-status-tag pt-status-tag-${t.status}`}>
                    {PLOT_THREAD_STATUS_LABELS[t.status]}
                  </span>
                </div>

                {/* 引文：编辑器里右键标记时存下的原文，回看时最容易唤起记忆 */}
                {t.quote && (
                  <blockquote className="pt-item-quote">
                    <Quote size={11} className="pt-quote-icon" />
                    <span>{t.quote}</span>
                  </blockquote>
                )}

                {t.detail && <p className="pt-item-detail">{t.detail}</p>}

                <div className="pt-item-refs">
                  {t.planted_chapter_id && (
                    <button
                      className="pt-ref"
                      title={t.quote ? '跳转到埋点章节并定位原文' : '跳转到埋点章节'}
                      onClick={() => openInEditor(t)}
                    >
                      <Bookmark size={11} /> 埋于 {chapterLabel(t.planted_chapter_id)}
                    </button>
                  )}

                  {t.status === "resolved" && t.resolved_chapter_id && (
                    <span className="pt-ref pt-ref-static">已回收于 {chapterLabel(t.resolved_chapter_id)}</span>
                  )}
                </div>

                {/* 生命周期操作：直接显示，不再需要展开。这些按钮本身都是刻意点击的动作，
                    误触风险低，而"先展开再点"只是多一步。 */}
                <div className="pt-item-bar">
                  {t.status === "open" ? (
                    <>
                      <button
                        className="pt-bar-btn pt-bar-primary"
                        title={resolveHint}
                        aria-label="标记为已回收"
                        onClick={() => void markResolved(t, selectedChapter?.id ?? null)}
                      >
                        <Check size={13} /> 回收
                      </button>
                      <button className="pt-bar-btn" onClick={() => void drop(t)}>
                        <X size={13} /> 废弃
                      </button>
                    </>
                  ) : (
                    <button className="pt-bar-btn" onClick={() => void markOpen(t)}>
                      <RotateCcw size={13} /> 恢复未回收
                    </button>
                  )}

                  {t.status === "resolved" && (
                    <label className="pt-bar-field">
                      改回收章节
                      <select
                        className="pt-select pt-select-inline"
                        value={t.resolved_chapter_id ?? ""}
                        aria-label="回收章节"
                        onChange={(e) => void markResolved(t, e.target.value || null)}
                      >
                        <option value="">未指定</option>
                        {chapters.map((c) => (
                          <option key={c.id} value={c.id}>第{c.chapter_number}章 {c.title}</option>
                        ))}
                      </select>
                    </label>
                  )}

                  <span className="pt-bar-spacer" />

                  <button className="pt-bar-btn" onClick={() => startEdit(t)}>
                    <Pencil size={13} /> 编辑
                  </button>
                  <button className="pt-bar-btn pt-bar-danger" onClick={() => void remove(t)}>
                    <Trash2 size={13} /> 删除
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
